import type { IconButtonProps } from '@mui/material/IconButton';
import type { TooltipProps } from '@mui/material/Tooltip';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import React, { forwardRef, memo, useId } from 'react';

export interface CustomTooltipProps {
  title: React.ReactNode;
  children?: React.ReactElement;
  placement?: TooltipProps['placement'];
  enterDelay?: number;
  leaveDelay?: number;
  maxWidth?: number;
  iconSize?: IconButtonProps['size'];
  intent?: 'info' | 'help' | 'warning';
  ariaLabel?: string;
  disableInteractive?: boolean;
  icon?: React.ReactNode;
  iconButtonProps?: Omit<IconButtonProps, 'children' | 'size' | 'aria-label'>;
}

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[3],
    border: `1px solid ${theme.palette.divider}`,
    fontSize: 13,
    lineHeight: 1.5,
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 10,
    maxWidth: 360,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.white,
    '&::before': {
      boxShadow: theme.shadows[1],
      border: `1px solid ${theme.palette.divider}`,
    },
  },
}));

const MinimalIconButton = styled(IconButton)(({ theme }) => ({
  padding: 6,
  borderRadius: 10,
  color: theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const TitleRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const intentIconMap: Record<NonNullable<CustomTooltipProps['intent']>, React.ReactNode> = {
  info: <InfoOutlinedIcon fontSize="small" />,
  help: <HelpOutlineIcon fontSize="small" />,
  warning: <InfoOutlinedIcon fontSize="small" />,
};

const CustomTooltip = memo(
  forwardRef<HTMLButtonElement | HTMLDivElement, CustomTooltipProps>(
    (
      {
        title,
        children,
        placement = 'top',
        enterDelay = 250,
        leaveDelay = 80,
        maxWidth,
        iconSize = 'small',
        intent = 'help',
        ariaLabel = 'More info',
        disableInteractive,
        icon,
        iconButtonProps,
      },
      _ref,
    ) => {
      if (!title) return null;

      const id = useId();

      const slotProps: TooltipProps['slotProps'] = maxWidth
        ? {
            tooltip: {
              sx: { maxWidth },
            },
          }
        : undefined;

      const trigger = children ? (
        React.cloneElement(children as React.ReactElement<any>, {
          'aria-describedby': `${id}-tooltip`,
        })
      ) : (
        <MinimalIconButton size={iconSize} aria-label={ariaLabel} {...iconButtonProps}>
          {icon ?? intentIconMap[intent]}
        </MinimalIconButton>
      );

      const renderedTitle = (
        <TitleRow>
          <LightbulbOutlinedIcon fontSize="small" />
          <span>{title}</span>
        </TitleRow>
      );

      return (
        <LightTooltip
          title={renderedTitle}
          placement={placement}
          arrow
          enterDelay={enterDelay}
          leaveDelay={leaveDelay}
          disableInteractive={disableInteractive}
          slotProps={slotProps}
        >
          <div>{trigger}</div>
        </LightTooltip>
      );
    },
  ),
);

export default CustomTooltip;
